<template>
  <div :class="data && data.classnames ? data.classnames : ''">
    <ion-page @mousedown="handlePageClick">
      <div id="header-stripe">
        <div class="version-info">Version: {{ versionInfo() }}</div>
        <img :src="getLogo()" />
      </div>

      <ion-content>
        <div id="container" :class="{ 'vertical-center': data && data.vcenter, fullheight: fullheight }">
          <form>
            <grid :stepdata="data" />
          </form>
        </div>
      </ion-content>
      <ion-footer>
        <button-bar :stepdata="data" />
        <simple-keyboard v-if="forceKeyboard && focussedFieldname" @onChange="simpleKeyboardChange" @onKeyPress="simpleKeyboardKeyPress" :input="focussedFieldValue" />
      </ion-footer>
    </ion-page>
  </div>
</template>

<script lang="ts">
import { IonContent, IonPage, IonFooter } from "@ionic/vue";
import Grid from "@/components/grid.vue";
import ButtonBar from "@/components/buttonbar.vue";
import SimpleKeyboard from "@/components/simple_keyboard.vue";
import { defineComponent, onMounted, ref, computed, ComputedRef, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { SubmitStepAttrs, ThemeData } from "@/types";

export default defineComponent({
  name: "Home",
  components: {
    IonPage,
    IonContent,
    IonFooter,
    Grid,
    ButtonBar,
    SimpleKeyboard,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const terminalId = ref("");

    const data: ComputedRef<any> = computed(() => {
      return store.getters["main/getStepData"];
    });

    const forceKeyboard: ComputedRef<any> = computed(() => {
      return store.getters["main/getForceKeyboard"];
    });

    const focussedFieldname: ComputedRef<any> = computed(() => {
      return store.getters["main/getFocussedFieldname"];
    });

    const focussedFieldValue: ComputedRef<any> = computed(() => {
      return store.getters["main/getFocussedFieldValue"];
    });

    const fullheight: ComputedRef<any> = computed(() => {
      return store.getters["main/getFullheight"];
    });

    const submitStepAttrs: ComputedRef<SubmitStepAttrs | null> = computed(() => {
      return store.getters["main/getSubmitStepAttrs"];
    });

    const loadingDialogPresent: ComputedRef<boolean> = computed(() => {
      return store.getters["main/getLoadingDialogPresent"];
    });

    const loadingControllers: ComputedRef<any[]> = computed(() => {
      return store.getters["main/getLoadingControllers"]
    })


    const versionInfo = () => {
      return store.getters["main/getAppVersion"];
    };

    const getLogo = () => {
      const theme = store.getters["main/getThemeData"] as ThemeData;
      if (theme && theme.logo) {
        return theme.logo;
      }
      return "/img/logo.png";
    };

    const simpleKeyboardChange = (input: string) => {
      store.commit("main/setFocussedFieldValue", input);
    };

    const simpleKeyboardKeyPress = () => {
      // console.log("SK_Button_>", button);
    };

    const handlePageClick = (e: MouseEvent) => {
      const elm = e.target as HTMLElement;
      const elmStr = `${elm.nodeName}.${elm.className}`;
      store.commit("main/setBodyClickEventTargetNodeName", elmStr);
      if (
        !elmStr.includes("simple-keyboard") &&
        !elmStr.includes("hg-button") &&
        !elmStr.includes("hg-row") &&
        !elmStr.includes("app-navigation-button") &&
        !elmStr.includes("native-input")
      ) {
        store.commit("main/setFocussedFieldname");
      }
    };

    const resetLoadingControllers = () => {
      if (loadingControllers.value.length) {
        for (const lc of loadingControllers.value) {
          lc.dismiss()
        }
      }
      store.commit("main/setLoadingControllers")
      const loadingElement = document.querySelectorAll("ion-loading")
      loadingElement.forEach((lc) => {
        lc.remove()
      })
    }

    watch(submitStepAttrs, async (submitStepAttrs) => {
      if (submitStepAttrs) {
        const url = submitStepAttrs.url;
        const loadingmsg = submitStepAttrs.loadingmsg;
        if ((await store.dispatch("main/countErrors")) === 0) {
          if (loadingmsg) {
            // Look down to the loadingDialogPresent watcher. It does the rest.
            store.commit("main/setLoadingData", loadingmsg);
          } else {
            await store.dispatch("main/updateStepData", url);
          }
        } else {
          store.commit("main/setSubmitStepAttrs", null);
        }
      }
    });

    watch(loadingDialogPresent, async (ldPresent) => {
      if (ldPresent && submitStepAttrs.value) {
        const url = submitStepAttrs.value.url;
        await store.dispatch("main/updateStepData", url);
      }
    });

    onMounted(async () => {
      resetLoadingControllers()
      terminalId.value = router.currentRoute.value.query.terminal as string;
      await store.dispatch("main/updateStepData");
    });
    return {
      store,
      data,
      versionInfo,
      getLogo,
      fullheight,
      simpleKeyboardChange,
      simpleKeyboardKeyPress,
      forceKeyboard,
      focussedFieldname,
      focussedFieldValue,
      handlePageClick,
    };
  },
});
</script>

<style lang="scss" scoped>
#header-stripe {
  position: relative;
  z-index: 1000;
  text-align: right;
  padding: 1.1vh 2.1vw;
  max-height: 8vh;
  border-bottom: 1px solid #999;
  background: rgb(239, 239, 239);
  background: -moz-linear-gradient(top, rgb(239, 239, 239) 0%, rgb(219, 219, 219) 100%);
  background: -webkit-linear-gradient(top, rgb(239, 239, 239) 0%, rgb(219, 219, 219) 100%);
  background: linear-gradient(to bottom, rgb(239, 239, 239) 0%, rgb(219, 219, 219) 100%);
  filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#efefef', endColorstr='#dbdbdb', GradientType=0);

  img {
    max-height: 100%;
  }

  .version-info {
    position: absolute;
    left: 0;
    bottom: 0;
    font-size: 0.8rem;
    color: #666;
    padding: 0 0 1.1vh 2vw;
  }
}

ion-content {
  --offset-top: 1px !important;
}

#container {
  text-align: center;
}

.vertical-center {
  position: relative;
  top: 50%;
  transform: translateY(-60%);
}

#container strong {
  font-size: 1.2rem;
  line-height: 2rem;
}

#container h1 {
  margin: 1.5rem 0 1.5rem;
  font-size: 2rem;
  line-height: 1.5;
}

#container p {
  font-size: 1.2rem;
  line-height: 2rem;

  color: #666;
  margin: 0 2vw 3vh;
}

#container a {
  text-decoration: none;
}
</style>